import type { IntlShape } from 'react-intl';
import * as yup from 'yup';
import type { DiscountClubFieldValue, DiscountClubFormField } from 'api/generated/types.gen';
import { isPatternMaskComplete } from 'lib/helpers/input-mask';
export type DiscountClubFormValues = Record<string, string | undefined>;

/**
 * Validation for a club's backend-defined form: required fields, a filled-in
 * mask, and the field's own pattern, whose message the backend supplies.
 */
export function buildDiscountClubSchema(fields: DiscountClubFormField[], intl: IntlShape) {
  const shape: Record<string, yup.StringSchema> = {};
  fields.forEach(field => {
    if (!field.key) return;
    const label = field.label ?? field.key;
    let schema = yup.string().trim();
    if (field.required) {
      schema = schema.required(intl.formatMessage({
        id: "validation.fieldRequired",
        defaultMessage: [{
          "type": 1,
          "value": "field"
        }, {
          "type": 0,
          "value": " is required"
        }]
      }, {
        field: label
      }));
    }
    const mask = field.mask;
    if (mask) {
      schema = schema.test('mask-complete', intl.formatMessage({
        id: "cart.discountClub.fieldIncomplete",
        defaultMessage: [{
          "type": 1,
          "value": "field"
        }, {
          "type": 0,
          "value": " is incomplete"
        }]
      }, {
        field: label
      }), value => !value || isPatternMaskComplete(value, mask));
    }
    const validation = field.validation;
    if (validation?.pattern) {
      schema = schema.matches(new RegExp(validation.pattern), {
        message: validation.errorMessage || undefined,
        excludeEmptyString: true
      });
    }
    shape[field.key] = schema;
  });
  return yup.object(shape).required();
}
export const emptyDiscountClubValues = (fields: DiscountClubFormField[]): DiscountClubFormValues => Object.fromEntries(fields.filter(field => field.key).map(field => [field.key, '']));

/** The request body's fields. Values are sent as displayed, separators
 *  included — the backend checks them against the mask's format. */
export const toDiscountClubFields = (fields: DiscountClubFormField[], values: DiscountClubFormValues): DiscountClubFieldValue[] => fields.filter(field => field.key).map(field => ({
  key: field.key,
  value: (values[field.key ?? ''] ?? '').trim()
}));

/** An SVG logo as an image source. Loaded through `<img>`, markup from the
 *  backend can't run scripts or touch the page. */
export const svgDataUri = (svg?: string | null) => svg ? `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}` : undefined;